/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useRouter } from 'next/router';
import { Fragment, useState } from 'react';
import styled from 'styled-components';
import { mqFrom } from 'styles/styles';
import i18n, { SettingsType } from 'utils/i18n';
import Button from './button';
import Confetti from './confetti';
import Modal from 'components/modal';
import ExperiencePointsSvg from 'components/experience-points-svg';

type QuizFormProps = {
  assessmentId?: string;
  quiz: QuizItem[] | undefined;
  onComplete: () => void;
  nextUnit: string;
};

type Selected = {
  id: string;
  showResult: boolean;
};

const QuizForm: React.FC<QuizFormProps> = ({ quiz = [], onComplete, nextUnit }) => {
  const [selected, setSelected] = useState<Record<string, Selected>>({});
  const [showConfetti, setShowConfetti] = useState(false);
  const [mistakesCount, setMistakesCount] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [validationText, setValidationText] = useState('');
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;
  const [showModal, setShowModal] = useState<'Congratulations' | ''>('');

  const selectAnswer = (questionId: string, answerId: string): void => {
    setSelected({ ...selected, [questionId]: { id: answerId, showResult: false } });
  };

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    let hasWrongAnswer = false;
    let hasUnselected = false;
    let mistakes = 0;

    quiz.forEach((q) => {
      const selectedAnswer = selected[q.id];
      if (!selectedAnswer) {
        hasUnselected = true;
        hasWrongAnswer = true;
        mistakes++;
      }
      const answer = q.answers.find((a) => a.id === selectedAnswer?.id);
      if (!answer?.isValid) {
        hasWrongAnswer = true;
        mistakes++;
      }
    });

    if (hasUnselected) {
      return setValidationText(dictionary.quizValidationAllFieldsAreRequired);
    } else {
      // Sæt showResult til true på alle svar
      const newSelected = Object.keys(selected).reduce((prev, key) => {
        return { ...prev, [key]: { ...selected[key], showResult: true } };
      }, {});

      setValidationText('');
      setSelected(newSelected);
    }

    if (!hasWrongAnswer) {
      setShowConfetti(true);
    }

    setMistakesCount(mistakes);
    setCompleted(true);
    onComplete();
    setShowModal('Congratulations');
  };

  if (!quiz.length) return null;

  const headline = (mistakes: number): string => {
    let headline;

    switch (true) {
      case mistakes === 0:
        headline = dictionary.wellDone;
        break;
      case mistakes === 1:
        headline = dictionary.soClose;
        break;
      default:
        headline = dictionary.notQuite;
    }

    return headline;
  };

  return (
    <StyledQuizForm>
      <form onSubmit={onSubmit}>
        <div className="questions">
          {quiz.map((question, index) => {
            return (
              <div key={question.id} className="question">
                <div className="label">
                  <span>{index + 1}.</span> {question.label}
                </div>
                <div className="answers">
                  {question.answers.map((answer) => {
                    const isSelected = selected[question.id]?.id === answer.id;
                    const showResult = isSelected && selected[question.id]?.showResult;
                    const isCorrect = showResult && answer.isValid;
                    const isWrong = showResult && !answer.isValid;
                    const showRightAnswer = completed && answer.isValid;

                    return (
                      <Fragment key={answer.id}>
                        <div
                          role="radio"
                          aria-checked={isSelected ? 'true' : 'false'}
                          className={`answer ${isSelected ? 'selected' : ''} 
                          ${completed ? 'no-hover' : ''}
                          ${isCorrect || showRightAnswer ? 'is-correct' : ''} 
                          ${isWrong ? 'is-wrong' : ''}`}
                          {...(!completed && { onClick: () => selectAnswer(question.id, answer.id) })}
                          {...(!completed && { onKeyUp: () => selectAnswer(question.id, answer.id) })}
                          tabIndex={0}
                        >
                          <div className="radio"></div>
                          <div className="label">{answer.label}</div>
                        </div>
                        {((showResult && answer.message) || (showRightAnswer && answer.message)) && (
                          <div
                            className={`message ${showRightAnswer ? 'is-correct' : ''} ${isWrong ? 'is-wrong' : ''}`}
                          >
                            {answer.message}
                          </div>
                        )}
                      </Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {!completed && (
          <div className="buttons">
            <Button mode="primary" htmlType="submit">
              {dictionary.quizCheckAnswers}
            </Button>
          </div>
        )}
        <Modal
          visible={showModal === 'Congratulations'}
          onClose={() => setShowModal('')}
          headline={headline(mistakesCount)}
          center
        >
          <p>
            {dictionary.completedKnowledgeCheck} <br />
            {dictionary.beforeContinue}{' '}
            <span className="fake-link" onClick={() => setShowModal('')}>
              {dictionary.quizReviewAnswers}
            </span>
            .
          </p>
          {mistakesCount === 0 && <ExperiencePointsSvg text={dictionary.completed} short={locale === 'de'} />}
          <div className="buttons">
            <Button mode="primary" iconName="chevron-right" alignIcon="right" linkProps={{ href: nextUnit }}>
              {dictionary.continue}
            </Button>
          </div>
        </Modal>
        {validationText && <div className="validation-text">{validationText}</div>}
        {showConfetti && <Confetti onComplete={() => setShowConfetti(false)} />}
      </form>
    </StyledQuizForm>
  );
};

export const StyledQuizForm = styled.ul`
  margin: 1rem 0;

  .question {
    margin: 1.5rem 0 1rem 0;

    > .label {
      span {
        font-weight: 600;
      }
    }
  }

  .answers {
    margin: 0.5rem 0;
    ${mqFrom.tablet} {
      margin: 0.5rem 3rem 0 3rem;
    }
  }

  .answer {
    padding: 0.5rem 1rem;

    margin-bottom: 0.5rem;
    position: relative;
    display: flex;
    align-items: center;
    background-color: var(--body-background-medium);
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;

    ${mqFrom.tablet} {
      background-color: transparent;
      border-radius: 1rem;
      padding: 0 1rem;
    }

    &:hover {
      background-color: transparent;
      border-color: transparent;
      box-shadow: 0 0 5px 0 var(--box-shadow-light);
    }

    .radio {
      width: 14px;
      height: 14px;
      border: 1px solid var(--text);
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-right: 1rem;
    }

    > .label {
      flex: 1 1 0;
    }

    .message {
      font-weight: 600;
      margin: 0.25rem 0 0.5rem 3rem;
      padding-left: 1rem;
    }

    &.selected {
      background-color: var(--primary-background);
      border-color: var(--primary-base);

      .radio {
        border: 1px solid var(--primary-base);

        &:before {
          content: '';
          /* display: block; */
          width: 6px;
          height: 6px;
          background: var(--primary-base);
          border-radius: 50%;
        }
      }
    }

    &.is-correct {
      background-color: var(--success-background);
      border-color: var(--success-base);
      margin-bottom: 0;

      .message {
        color: var(--success-base);
      }
    }

    &.is-wrong {
      background-color: var(--danger-background);
      border-color: var(--danger-base);
      margin-bottom: 0;
      .message {
        color: var(--danger-base);
      }
    }
  }

  .message {
    font-weight: 600;
    margin: 0.25rem 0 0.5rem 3rem;
    padding-left: 1rem;

    &.is-correct {
      color: var(--success-base);
    }

    &.is-wrong {
      color: var(--danger-base);
    }
  }

  .buttons {
    margin: 1rem 0;
  }

  .validation-text {
    margin-top: 0.5rem;
    color: var(--danger-base);
    font-weight: 600;
    font-size: 0.875rem;
  }

  .fake-link {
    color: var(--primary-base);
    cursor: pointer;
    text-decoration: none;
    word-wrap: break-word;
    /* text-transform: lowercase; */
  }

  .fake-link:hover {
    color: var(--primary-hover);
    text-decoration: underline;
  }

  .fake-link:visited {
    color: var(--visited);
  }
`;

export default QuizForm;
